/**
 * Mobile Navigation Component
 * Provides mobile-friendly navigation for authenticated users
 */

import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { clsx } from 'clsx';
import { Menu, X } from 'lucide-react';
import OfflineStatusIndicator from '@/components/UI/OfflineStatusIndicator';
import {
  Home,
  Dumbbell,
  TrendingUp,
  User,
  BookOpen,
  Watch,
  Calendar,
  FileText,
  Users
} from 'lucide-react';

const navigation = [
  { name: 'Dashboard', href: '/app/dashboard', icon: Home },
  { name: 'Calendar', href: '/app/calendar', icon: Calendar },
  { name: 'Workouts', href: '/app/workout/1/monday-am', icon: Dumbbell },
  { name: 'Templates', href: '/app/templates', icon: FileText },
  { name: 'How-to\'s', href: '/app/workout-howtos', icon: BookOpen },
  { name: 'Social', href: '/app/social', icon: Users },
  { name: 'Progress', href: '/app/progress', icon: TrendingUp },
  { name: 'Wearables', href: '/app/wearables', icon: Watch },
  { name: 'Profile', href: '/app/profile/overview', icon: User },
];

export default function MobileNavigation() {
  const [isOpen, setIsOpen] = useState(false);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const closeMenu = () => {
    setIsOpen(false);
  };

  return (
    <>
      {/* Mobile menu button - only visible on mobile */}
      <div className="lg:hidden fixed bottom-4 right-4 z-50">
        <button
          type="button"
          onClick={toggleMenu}
          className="bg-primary-600 text-white p-3 rounded-full shadow-lg hover:bg-primary-700 transition-colors"
          aria-label="Toggle navigation menu"
        >
          {isOpen ? (
            <X className="h-6 w-6" />
          ) : (
            <Menu className="h-6 w-6" />
          )}
        </button>
      </div>

      {/* Mobile menu overlay */}
      {isOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black bg-opacity-50" onClick={closeMenu} />
      )}

      {/* Mobile menu panel */}
      <div
        className={clsx(
          'lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 transform transition-transform duration-300 ease-in-out',
          isOpen ? 'translate-y-0' : 'translate-y-full'
        )}
      >
        <div className="px-4 py-6">
          <div className="text-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Navigation</h2>
          </div>
          
          <nav className="grid grid-cols-3 gap-4">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                onClick={closeMenu}
                className={({ isActive }) =>
                  clsx(
                    'flex flex-col items-center p-3 rounded-lg transition-colors text-center',
                    isActive
                      ? 'bg-primary-100 text-primary-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <item.icon
                      className={clsx(
                        'h-6 w-6 mb-1',
                        isActive
                          ? 'text-primary-600'
                          : 'text-gray-400'
                      )}
                    />
                    <span className="text-xs font-medium">{item.name}</span>
                  </>
                )}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>
    </>
  );
}

/**
 * Bottom Tab Navigation (Alternative mobile navigation style)
 */
export function BottomTabNavigation() {
  // Show only the most important navigation items for bottom tabs
  const primaryNavigation = [
    { name: 'Dashboard', href: '/app/dashboard', icon: Home },
    { name: 'Workouts', href: '/app/workout/1/monday-am', icon: Dumbbell },
    { name: 'Progress', href: '/app/progress', icon: TrendingUp },
    { name: 'Social', href: '/app/social', icon: Users },
    { name: 'Profile', href: '/app/profile/overview', icon: User },
  ];

  return (
    <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 safe-area-pb">
      {/* Sync Status Indicator - Bottom Left Corner */}
      <div className="absolute bottom-full left-2 mb-1">
        <OfflineStatusIndicator navigationMode className="w-auto" />
      </div>

      <nav className="flex justify-around py-2">
        {primaryNavigation.map((item) => (
          <NavLink
            key={item.name}
            to={item.href}
            className={({ isActive }) =>
              clsx(
                'flex flex-col items-center p-2 min-w-0 flex-1 transition-colors',
                isActive
                  ? 'text-primary-600'
                  : 'text-gray-400 hover:text-gray-600'
              )
            }
          >
            {({ isActive }) => (
              <>
                <item.icon
                  className={clsx(
                    'h-5 w-5 mb-1',
                    isActive
                      ? 'text-primary-600'
                      : 'text-gray-400'
                  )}
                />
                <span className="text-xs font-medium truncate">{item.name}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

/**
 * Mobile Navigation Drawer (Slide-in from side)
 */
export function MobileNavigationDrawer() {
  const [isOpen, setIsOpen] = useState(false);

  const toggleDrawer = () => {
    setIsOpen(!isOpen);
  };

  const closeDrawer = () => {
    setIsOpen(false);
  };

  return (
    <>
      {/* Mobile menu button */}
      <div className="lg:hidden">
        <button
          type="button"
          onClick={toggleDrawer}
          className="text-gray-700 hover:text-gray-900 p-2"
          aria-label="Open navigation menu"
        >
          <Menu className="h-6 w-6" />
        </button>
      </div>

      {/* Overlay */}
      {isOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-40 bg-black bg-opacity-50" 
          onClick={closeDrawer} 
        />
      )}

      {/* Drawer */}
      <div
        className={clsx(
          'lg:hidden fixed top-0 left-0 bottom-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Menu</h2>
          <button
            type="button"
            onClick={closeDrawer}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <nav className="mt-4 px-2">
          {navigation.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              onClick={closeDrawer}
              className={({ isActive }) =>
                clsx(
                  'group flex items-center px-2 py-3 text-sm font-medium rounded-md transition-colors mb-1',
                  isActive
                    ? 'bg-primary-100 text-primary-900'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                )
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon
                    className={clsx(
                      'mr-3 flex-shrink-0 h-5 w-5',
                      isActive
                        ? 'text-primary-500'
                        : 'text-gray-400 group-hover:text-gray-500'
                    )}
                  />
                  {item.name}
                </>
              )}
            </NavLink>
          ))}
        </nav>
      </div>
    </>
  );
}
